/**
 * Cross-platform input layer. Detects whether the device prefers
 * mouse/keyboard, touch, or both. Exposes hooks for binding keyboard
 * shortcuts and rendering control hints. Games consume this so they
 * can declare bindings once and surface a HUD that adapts per device.
 *
 * Also home of the shell-rendered controls layer (Total-Redesign-Spec
 * §1c): `createControlsLayer` renders joystick / action-button / aux
 * widgets inside the game host for games whose manifest declares a
 * `controlScheme`. Legacy paths (createJoystick, bindKeys, …) are kept
 * untouched for the 30 existing games.
 */
import type { ButtonSpec, ControlScheme, ControlsAuxEvent, GameControls } from '@tt/types';

export type Platform = 'desktop' | 'tablet' | 'phone' | 'unknown';

export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'unknown';
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const hover = window.matchMedia?.('(any-hover: hover)').matches ?? false;
  const ua = navigator.userAgent || '';
  const isPhone = /iPhone|Android.+Mobile|Mobile|iemobile/i.test(ua);
  const isTablet = /iPad|Android(?!.+Mobile)|Tablet/i.test(ua);
  if (isPhone) return 'phone';
  if (isTablet) return 'tablet';
  if (!coarse && hover) return 'desktop';
  if (coarse && !hover) return 'phone';
  return 'desktop';
}

export function hasKeyboard(p: Platform = detectPlatform()): boolean {
  return p === 'desktop';
}

export function hasTouch(): boolean {
  if (typeof window === 'undefined') return false;
  return ('ontouchstart' in window) || (navigator.maxTouchPoints ?? 0) > 0;
}

export function vibrate(pattern: number | number[]): void {
  if (typeof navigator === 'undefined') return;
  const n = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
  try { n.vibrate?.(pattern); } catch { /* ignore */ }
}

export interface InputBindingHint {
  /** Display label ("Click", "Space", "Tap", "1-4") */
  label: string;
  /** Concrete keyboard key used for keyboard play */
  key?: string | string[];
}

export interface InputBindings {
  primary: InputBindingHint;
  alt?: InputBindingHint[];
}

/**
 * Default bindings per control scheme. Games can override on their
 * manifest's `inputBindings` field.
 */
export function defaultBindings(controls: string[]): InputBindings {
  if (controls.includes('tap')) {
    return {
      primary: { label: 'Tap / Click', key: ['Space', 'Enter'] },
      alt: [{ label: 'Space', key: 'Space' }, { label: 'Enter', key: 'Enter' }],
    };
  }
  if (controls.includes('swipe')) {
    return {
      primary: { label: 'Swipe', key: ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'] },
      alt: [{ label: 'Arrow keys' }],
    };
  }
  return { primary: { label: 'Tap / Click' } };
}

// ── Drag tracking ──────────────────────────────────────────────────────

export interface DragEvent {
  type: 'start' | 'move' | 'end';
  x: number;
  y: number;
  t: number;
  dx: number;
  dy: number;
}

export function bindDrag(target: HTMLElement, onEvent: (e: DragEvent) => void): () => void {
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let activeId: number | null = null;

  function localXY(ev: PointerEvent): { x: number; y: number } {
    const rect = target.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  }
  function down(ev: PointerEvent) {
    if (activeId !== null) return;
    activeId = ev.pointerId;
    target.setPointerCapture?.(ev.pointerId);
    dragging = true;
    const p = localXY(ev);
    lastX = p.x; lastY = p.y;
    onEvent({ type: 'start', x: p.x, y: p.y, t: ev.timeStamp, dx: 0, dy: 0 });
    ev.preventDefault();
  }
  function move(ev: PointerEvent) {
    if (!dragging || ev.pointerId !== activeId) return;
    const p = localXY(ev);
    onEvent({ type: 'move', x: p.x, y: p.y, t: ev.timeStamp, dx: p.x - lastX, dy: p.y - lastY });
    lastX = p.x; lastY = p.y;
    ev.preventDefault();
  }
  function up(ev: PointerEvent) {
    if (!dragging || ev.pointerId !== activeId) return;
    const p = localXY(ev);
    onEvent({ type: 'end', x: p.x, y: p.y, t: ev.timeStamp, dx: 0, dy: 0 });
    dragging = false;
    activeId = null;
    try { target.releasePointerCapture?.(ev.pointerId); } catch { /* */ }
  }
  target.addEventListener('pointerdown', down);
  target.addEventListener('pointermove', move);
  target.addEventListener('pointerup', up);
  target.addEventListener('pointercancel', up);
  // Window-level fallback so a pointerup that fires off the target (finger
  // dragged past the canvas, lost focus mid-drag, browser pointer-capture
  // glitch) still ends the stroke. Without this, the stroke could remain
  // "active" forever and onEnd would never fire — explaining the "I traced
  // perfectly but no score came back" symptom in trace-race.
  const onWindowUp = (ev: PointerEvent) => {
    if (dragging && ev.pointerId === activeId) up(ev);
  };
  window.addEventListener('pointerup', onWindowUp);
  window.addEventListener('pointercancel', onWindowUp);
  return () => {
    target.removeEventListener('pointerdown', down);
    target.removeEventListener('pointermove', move);
    target.removeEventListener('pointerup', up);
    target.removeEventListener('pointercancel', up);
    window.removeEventListener('pointerup', onWindowUp);
    window.removeEventListener('pointercancel', onWindowUp);
  };
}

// ── Stroke (path) capture ──────────────────────────────────────────────

export interface StrokePoint { x: number; y: number; t: number }

export function bindStroke(
  target: HTMLElement,
  onPoint: (p: StrokePoint) => void,
  onEnd: (stroke: StrokePoint[]) => void,
): () => void {
  let stroke: StrokePoint[] = [];
  return bindDrag(target, (ev) => {
    if (ev.type === 'start') {
      stroke = [{ x: ev.x, y: ev.y, t: ev.t }];
      onPoint(stroke[0]!);
    } else if (ev.type === 'move') {
      const p = { x: ev.x, y: ev.y, t: ev.t };
      stroke.push(p);
      onPoint(p);
    } else {
      onEnd(stroke);
      stroke = [];
    }
  });
}

/** Convert a KeyboardEvent.code into a Display label. */
export function prettyKey(code: string): string {
  switch (code) {
    case 'Space': return 'Space';
    case 'Enter': return 'Enter';
    case 'Escape': return 'Esc';
    case 'ArrowUp': return '↑';
    case 'ArrowDown': return '↓';
    case 'ArrowLeft': return '←';
    case 'ArrowRight': return '→';
    case 'Digit1': return '1';
    case 'Digit2': return '2';
    case 'Digit3': return '3';
    case 'Digit4': return '4';
    case 'KeyR': return 'R';
    case 'KeyV': return 'V';
    case 'KeyS': return 'S';
    default: return code;
  }
}

/**
 * Subscribe to keyboard events globally. Returns an unsubscribe.
 * Bindings are matched against KeyboardEvent.code (so layout-independent).
 */
export function bindKeys(map: Record<string, () => void>): () => void {
  const handler = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    const fn = map[e.code];
    if (!fn) return;
    e.preventDefault();
    fn();
  };
  window.addEventListener('keydown', handler);
  return () => window.removeEventListener('keydown', handler);
}

// ── Held-key state (for joystick / movement on desktop) ────────────────

export interface HeldKeys {
  /** Snapshot of currently-pressed codes. */
  has(code: string): boolean;
  /** Returns -1, 0, or 1 from a {neg, pos} key pair. */
  axis(neg: string, pos: string): number;
  dispose(): void;
}

/**
 * Keys that, when handled by an active game, should NOT trigger
 * the browser default (page scroll on arrows/space, form submit on
 * enter). Used by `bindHeldKeys` + `bindKeys` to call preventDefault
 * automatically while a game is mounted. Scoped per-handler (not
 * global) so the home/lobby pages still scroll normally.
 */
const GAME_KEYS_TO_BLOCK = new Set<string>([
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Space',
  'KeyW', 'KeyA', 'KeyS', 'KeyD',
]);

export function bindHeldKeys(): HeldKeys {
  const down = new Set<string>();
  const onDown = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    // Stop the browser from scrolling the page when arrows / space /
    // WASD are pressed while a game is mounted. bindHeldKeys is only
    // bound for the lifetime of a match (games dispose it on end),
    // so the home/lobby pages remain unaffected.
    if (GAME_KEYS_TO_BLOCK.has(e.code)) e.preventDefault();
    down.add(e.code);
  };
  const onUp = (e: KeyboardEvent) => { down.delete(e.code); };
  const onBlur = () => { down.clear(); };
  window.addEventListener('keydown', onDown);
  window.addEventListener('keyup', onUp);
  window.addEventListener('blur', onBlur);
  return {
    has: (c: string) => down.has(c),
    axis: (neg: string, pos: string) => (down.has(pos) ? 1 : 0) - (down.has(neg) ? 1 : 0),
    dispose: () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    },
  };
}

// ── Virtual joystick ───────────────────────────────────────────────────

export interface JoystickState {
  /** Current normalized direction (-1..1 each axis). 0/0 when released. */
  x: number;
  y: number;
  /** Magnitude 0..1. */
  mag: number;
  /** Whether finger is currently down. */
  active: boolean;
}

export interface JoystickOptions {
  /** Max radius the thumb can travel from origin (px). Default 64. */
  radius?: number;
  /** Optional touch dead-zone (mag < this snaps to 0). Default 0.08. */
  deadzone?: number;
  /** Optional held-keys for keyboard fallback (WASD + arrows). */
  keys?: HeldKeys;
  /** Optional callback fired on each pointer/key update. */
  onChange?: (s: JoystickState) => void;
}

export interface JoystickCoreOptions {
  /** Max radius the thumb can travel from origin (px). Default 64. */
  radius?: number;
  /** Touch dead-zone (mag < this snaps to 0). Default 0.08. */
  deadzone?: number;
  /** Optional held-keys for keyboard fallback (WASD + arrows). */
  keys?: HeldKeys;
  /** Fired on each pointer/key update. */
  onChange?: (s: JoystickState) => void;
  /** Mutate this state object instead of allocating one (lets a host keep
   *  a stable reference across widget rebuilds). */
  state?: JoystickState;
  /** Lock movement to the x axis (slider-rail / joystick-x variant). */
  xOnly?: boolean;
  /**
   * Decide whether a pointerdown starts a stick gesture. Return the origin
   * point (client px) to use, or null to ignore the touch. Default policy:
   * reject mouse pointers, origin at the raw touch point (legacy
   * floating-origin behavior).
   */
  acceptDown?: (ev: PointerEvent) => { x: number; y: number } | null;
  /** Visual hooks (client px). The core owns NO DOM — callers render. */
  onShow?: (originX: number, originY: number) => void;
  onThumb?: (x: number, y: number) => void;
  onHide?: () => void;
}

/**
 * Pointer + keyboard state math shared by `createJoystick` (legacy,
 * document.body-rendered) and the shell controls layer's bounded-zone
 * joystick. Owns: pointer capture, normalization/deadzone, the window-level
 * pointerup fallback, the 800ms stuck-gesture watchdog, and the 33ms
 * keyboard tick. Owns NO DOM — visuals arrive via onShow/onThumb/onHide.
 */
export function createJoystickCore(target: HTMLElement, options: JoystickCoreOptions = {}): { state: JoystickState; dispose: () => void } {
  const radius = options.radius ?? 64;
  const deadzone = options.deadzone ?? 0.08;
  const state: JoystickState = options.state ?? { x: 0, y: 0, mag: 0, active: false };
  state.x = 0; state.y = 0; state.mag = 0; state.active = false;

  let activeId: number | null = null;
  let originX = 0;
  let originY = 0;

  const acceptDown = options.acceptDown
    ?? ((ev: PointerEvent) => (ev.pointerType === 'mouse' ? null : { x: ev.clientX, y: ev.clientY })); // mouse uses keyboard fallback

  function emit() {
    options.onChange?.(state);
  }

  function applyPointer(ev: PointerEvent) {
    const dx = ev.clientX - originX;
    const dy = options.xOnly ? 0 : ev.clientY - originY;
    const len = Math.sqrt(dx * dx + dy * dy);
    const clampedLen = Math.min(len, radius);
    const nx = len > 0 ? (dx / len) * (clampedLen / radius) : 0;
    const ny = len > 0 ? (dy / len) * (clampedLen / radius) : 0;
    state.x = nx;
    state.y = ny;
    state.mag = Math.min(1, len / radius);
    options.onThumb?.(originX + nx * radius, originY + ny * radius);
    if (state.mag < deadzone) { state.x = 0; state.y = 0; state.mag = 0; }
    emit();
  }

  let lastEmitAt = Date.now();
  function onDown(ev: PointerEvent) {
    if (activeId !== null) return;
    const origin = acceptDown(ev);
    if (!origin) return;
    activeId = ev.pointerId;
    lastEmitAt = Date.now();
    target.setPointerCapture?.(ev.pointerId);
    originX = origin.x;
    originY = origin.y;
    state.active = true;
    options.onShow?.(originX, originY);
    options.onThumb?.(originX, originY);
    ev.preventDefault();
    // Treat the touch itself as the first move so a re-origined stick
    // (controls layer clamps the origin) immediately reflects the offset.
    applyPointer(ev);
  }

  function onMove(ev: PointerEvent) {
    if (ev.pointerId !== activeId) return;
    lastEmitAt = Date.now();
    applyPointer(ev);
    ev.preventDefault();
  }

  function release() {
    activeId = null;
    state.x = 0; state.y = 0; state.mag = 0; state.active = false;
    options.onHide?.();
  }

  function onUp(ev: PointerEvent) {
    if (ev.pointerId !== activeId) return;
    release();
    try { target.releasePointerCapture?.(ev.pointerId); } catch { /* */ }
    emit();
  }

  target.addEventListener('pointerdown', onDown);
  target.addEventListener('pointermove', onMove);
  target.addEventListener('pointerup', onUp);
  target.addEventListener('pointercancel', onUp);
  // Also listen on the WINDOW so a pointerup that fires off the canvas
  // (finger dragged past the edge, window blurred mid-drag, browser
  // pointer-capture quirk) still clears `activeId`. Without this fallback
  // the joystick stayed locked to a stale pointerId and rejected every
  // future touch — the player appeared frozen even though their character
  // was alive on the server.
  const onWindowUp = (ev: PointerEvent) => {
    if (activeId !== null && ev.pointerId === activeId) onUp(ev);
  };
  window.addEventListener('pointerup', onWindowUp);
  window.addEventListener('pointercancel', onWindowUp);
  // Recovery watchdog: if the joystick believes it's active but no
  // pointermove has arrived for 800ms, force-release. Belt-and-suspenders
  // for the rare cases where neither target nor window get the up event.
  const watchdog = window.setInterval(() => {
    if (state.active && Date.now() - lastEmitAt > 800) {
      release();
    }
  }, 400);

  // Keyboard fallback driver — feeds the same JoystickState at 30 Hz.
  let keyTimer: number | null = null;
  if (options.keys) {
    const keys = options.keys;
    const tick = () => {
      // Only override if pointer is not active.
      if (!state.active) {
        const kx = keys.axis('KeyA', 'KeyD') + keys.axis('ArrowLeft', 'ArrowRight');
        const ky = options.xOnly ? 0 : keys.axis('KeyW', 'KeyS') + keys.axis('ArrowUp', 'ArrowDown');
        const cx = Math.max(-1, Math.min(1, kx));
        const cy = Math.max(-1, Math.min(1, ky));
        const mag = Math.sqrt(cx * cx + cy * cy);
        const norm = mag > 1 ? 1 / mag : 1;
        state.x = cx * norm;
        state.y = cy * norm;
        state.mag = Math.min(1, mag);
      }
      emit();
      keyTimer = window.setTimeout(tick, 33);
    };
    keyTimer = window.setTimeout(tick, 33);
  }

  return {
    state,
    dispose: () => {
      target.removeEventListener('pointerdown', onDown);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      window.removeEventListener('pointerup', onWindowUp);
      window.removeEventListener('pointercancel', onWindowUp);
      clearInterval(watchdog);
      if (keyTimer !== null) clearTimeout(keyTimer);
      state.x = 0; state.y = 0; state.mag = 0; state.active = false;
    },
  };
}

/**
 * Creates a floating-origin virtual joystick rooted at `target`. On touch,
 * the joystick origin is wherever the finger first lands; the visual base +
 * thumb DOM nodes are appended on demand. On desktop, WASD/arrow keys drive
 * the same JoystickState through the optional `keys` HeldKeys instance.
 *
 * LEGACY path (appends to document.body) — kept byte-compatible for the
 * pre-redesign games. New games get the shell controls layer instead
 * (createControlsLayer below), which shares the same state math via
 * createJoystickCore.
 *
 * Returns:
 *  - `state` — live mutable JoystickState (read each frame)
 *  - `dispose()` — removes listeners + DOM nodes
 */
export function createJoystick(target: HTMLElement, options: JoystickOptions = {}): { state: JoystickState; dispose: () => void } {
  const radius = options.radius ?? 64;

  const base = document.createElement('div');
  base.style.cssText = `
    position: absolute; pointer-events: none;
    width: ${radius * 2}px; height: ${radius * 2}px;
    border-radius: 50%; border: 2px solid rgba(248, 237, 214, 0.32);
    background: radial-gradient(circle, rgba(15,12,24,0.55) 0%, rgba(15,12,24,0.15) 100%);
    transform: translate(-50%, -50%); opacity: 0; transition: opacity 0.12s ease;
    z-index: 10;
  `;
  const thumb = document.createElement('div');
  thumb.style.cssText = `
    position: absolute; pointer-events: none;
    width: ${radius * 0.65}px; height: ${radius * 0.65}px;
    border-radius: 50%; background: rgba(248,237,214,0.85);
    box-shadow: 0 0 18px rgba(255, 200, 80, 0.5);
    transform: translate(-50%, -50%);
    z-index: 11;
  `;
  document.body.appendChild(base);
  document.body.appendChild(thumb);

  const core = createJoystickCore(target, {
    radius,
    ...(options.deadzone !== undefined ? { deadzone: options.deadzone } : {}),
    ...(options.keys ? { keys: options.keys } : {}),
    ...(options.onChange ? { onChange: options.onChange } : {}),
    onShow: (x, y) => {
      base.style.left = `${x}px`;
      base.style.top = `${y}px`;
      thumb.style.left = `${x}px`;
      thumb.style.top = `${y}px`;
      base.style.opacity = '1';
    },
    onThumb: (x, y) => {
      thumb.style.left = `${x}px`;
      thumb.style.top = `${y}px`;
    },
    onHide: () => {
      base.style.opacity = '0';
    },
  });

  return {
    state: core.state,
    dispose: () => {
      core.dispose();
      base.remove();
      thumb.remove();
    },
  };
}

// ── Shell controls layer (Total-Redesign-Spec §1c) ─────────────────────
//
// `<div class="controls-layer">` lives INSIDE the game host (never
// document.body), absolutely positioned inset:0 with pointer-events:none;
// individual widgets re-enable pointer events. Touch platforms get a
// bounded-zone floating joystick + tavern-styled action buttons; desktop
// gets key bindings + a dismissible hint strip. Styles in
// web/src/styles/global.css under "Shell controls layer".

type ActionSlot = 'primary' | 'secondary';
type HostRect = { x: number; y: number; width: number; height: number };
type StageRect = { x: number; y: number; w: number; h: number };

/** Placeholder glyphs for the manifest icon enum (real art in a later wave). */
const ICON_GLYPHS: Record<string, string> = {
  sword: '⚔', boot: '👢', flag: '⚑', drop: '⤓', jump: '⤒',
  check: '✔', eraser: '⌫', hammer: '🔨', flame: '🔥', bucket: '🪣', hand: '✋',
};

const DEFAULT_ACTION_KEYS: Record<ActionSlot, string[]> = {
  primary: ['Space', 'KeyJ'],
  secondary: ['ShiftLeft', 'KeyK'],
};

/** charge-mode buttons auto-release at this held duration. */
const CHARGE_MAX_MS = 1500;
/** Joystick widget geometry (spec §1c.2): 128px base / 56px thumb. */
const JOY_BASE = 128;
const JOY_THUMB_TRAVEL = (JOY_BASE - 56) / 2 + 8; // thumb travel radius px
/** joystick-x slider rail geometry. */
const RAIL_W = 176;
const RAIL_H = 48;
/** Floating-zone bounds: lower-left 45% × 40% of the host rect. */
const ZONE_W_FRAC = 0.45;
const ZONE_H_FRAC = 0.40;

export interface ControlsLayerOptions {
  platform: Platform;
  /** localStorage hint-dismiss key suffix: `tt-controls-hint-<gameId>`. */
  gameId?: string;
  onDestroy?: () => void;
}

export interface ControlsLayerHandle {
  controls: GameControls;
  layerEl: HTMLDivElement;
  destroy(): void;
  /** Union rect of the visible shell widgets in host-local CSS px, or null
   *  (desktop / no widgets). Feeds StageViewport.controlsRect. */
  getControlsRect(): HostRect | null;
  /** Install a stage-coords → host-CSS-px converter for reserved regions
   *  (games declare rects in stage coords; the shell clamps widgets in host
   *  px). Defaults to identity. */
  setStageTransform(fn: (r: StageRect) => StageRect): void;
}

interface EffectiveLayer {
  move: 'joystick' | 'joystick-x' | 'none';
  aux: 'tap-world' | 'tap-target' | 'drag' | 'draw' | 'typing' | 'none';
  primary: ButtonSpec | undefined;
  secondary: ButtonSpec | undefined;
  auxKeys: string[] | undefined;
}

function prettyKeyShort(code: string): string {
  if (code === 'ShiftLeft' || code === 'ShiftRight') return 'Shift';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  return prettyKey(code);
}

function isTypingTarget(e: Event): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
}

/** Push a circle center (cx, cy, radius R) out of any intersecting rect
 *  along the axis of least displacement. */
function pushOutsideRects(cx: number, cy: number, R: number, rects: StageRect[]): { x: number; y: number } {
  for (const r of rects) {
    const nx = Math.max(r.x, Math.min(cx, r.x + r.w));
    const ny = Math.max(r.y, Math.min(cy, r.y + r.h));
    const dx = cx - nx;
    const dy = cy - ny;
    if (dx * dx + dy * dy >= R * R) continue;
    const pushLeft = cx - (r.x - R);
    const pushRight = (r.x + r.w + R) - cx;
    const pushUp = cy - (r.y - R);
    const pushDown = (r.y + r.h + R) - cy;
    const min = Math.min(pushLeft, pushRight, pushUp, pushDown);
    if (min === pushLeft) cx = r.x - R;
    else if (min === pushRight) cx = r.x + r.w + R;
    else if (min === pushUp) cy = r.y - R;
    else cy = r.y + r.h + R;
  }
  return { x: cx, y: cy };
}

/**
 * Render the declarative `controlScheme` for one game inside `host`.
 * Returns a stable GameControls implementation (safe to hand to the game
 * before/across setPhase rebuilds) plus shell-side hooks.
 */
export function createControlsLayer(
  host: HTMLElement,
  scheme: ControlScheme,
  opts: ControlsLayerOptions,
): ControlsLayerHandle {
  const isTouch = opts.platform !== 'desktop';

  const layerEl = document.createElement('div');
  layerEl.className = 'controls-layer';
  host.appendChild(layerEl);

  // Tray is a permanent (hidden) container so openTray is cheap.
  const trayEl = document.createElement('div');
  trayEl.className = 'tt-tray';
  layerEl.appendChild(trayEl);

  // ── Stable cross-rebuild state ──────────────────────────────────────
  const moveState: JoystickState = { x: 0, y: 0, mag: 0, active: false };
  const actionSubs: Record<ActionSlot, { press: Set<(h?: number) => void>; release: Set<(h?: number) => void> }> = {
    primary: { press: new Set(), release: new Set() },
    secondary: { press: new Set(), release: new Set() },
  };
  const auxSubs = new Set<(e: ControlsAuxEvent) => void>();
  const buttonHidden: Record<ActionSlot, boolean> = { primary: false, secondary: false };
  let reserved: StageRect[] = [];
  let stageTransform: (r: StageRect) => StageRect = (r) => r;
  let currentPhase: string | null = null;
  let destroyed = false;

  // Movement keys are shared by every phase config; touch devices with a
  // keyboard attached (tablets, hybrids) benefit too.
  const heldKeys = bindHeldKeys();

  // Per-phase teardown (widgets, listeners, cores).
  const phaseCleanups: Array<() => void> = [];
  let joyEl: HTMLDivElement | null = null;
  let actionsEl: HTMLDivElement | null = null;
  const buttonEls: Record<ActionSlot, HTMLButtonElement | null> = { primary: null, secondary: null };
  let typingInput: HTMLInputElement | null = null;

  function emitAux(e: ControlsAuxEvent): void {
    for (const cb of [...auxSubs]) { try { cb(e); } catch { /* listener error */ } }
  }
  function emitAction(slot: ActionSlot, ev: 'press' | 'release', heldMs?: number): void {
    for (const cb of [...actionSubs[slot][ev]]) { try { cb(heldMs); } catch { /* listener error */ } }
  }
  function reservedHostRects(): StageRect[] {
    return reserved.map((r) => stageTransform(r));
  }
  function resolveLayer(phase: string | null): EffectiveLayer {
    const p = (phase ? scheme.phases?.[phase] : undefined) ?? {};
    // `??` fallbacks double as runtime defaults for raw (un-zod-parsed)
    // manifest JSON where `move`/`aux`/`mode` may be absent.
    return {
      move: p.move ?? scheme.move ?? 'none',
      aux: p.aux ?? scheme.aux ?? 'none',
      primary: p.primary ?? scheme.primary,
      secondary: p.secondary ?? scheme.secondary,
      auxKeys: p.auxKeys ?? scheme.auxKeys,
    };
  }

  // ── Movement widget ─────────────────────────────────────────────────
  function buildMove(eff: EffectiveLayer): void {
    if (eff.move === 'none') return;
    if (!isTouch) {
      // Desktop: no widget; WASD/arrows feed the same JoystickState.
      const core = createJoystickCore(layerEl, {
        state: moveState,
        keys: heldKeys,
        xOnly: eff.move === 'joystick-x',
        acceptDown: () => null,
      });
      phaseCleanups.push(() => core.dispose());
      return;
    }

    const xOnly = eff.move === 'joystick-x';
    const halfW = xOnly ? RAIL_W / 2 : JOY_BASE / 2;
    const halfH = xOnly ? RAIL_H / 2 : JOY_BASE / 2;
    const clampR = Math.max(halfW, halfH);

    // Bounded floating zone — touches in the lower-left 45%×40% of the
    // host re-origin the stick.
    const zone = document.createElement('div');
    zone.className = 'tt-joy-zone';
    zone.style.width = `${ZONE_W_FRAC * 100}%`;
    zone.style.height = `${ZONE_H_FRAC * 100}%`;
    layerEl.appendChild(zone);

    const widget = document.createElement('div');
    widget.className = xOnly ? 'tt-joy tt-joy-rail' : 'tt-joy';
    const thumb = document.createElement('div');
    thumb.className = 'tt-joy-thumb';
    widget.appendChild(thumb);
    layerEl.appendChild(widget);
    joyEl = widget;

    // Gesture-time widget center, client px (for thumb offsets).
    let centerClientX = 0;
    let centerClientY = 0;

    const core = createJoystickCore(zone, {
      state: moveState,
      keys: heldKeys,
      xOnly,
      radius: xOnly ? (RAIL_W - 56) / 2 : JOY_THUMB_TRAVEL,
      acceptDown: (ev) => {
        const hr = host.getBoundingClientRect();
        // Re-origin within the zone, clamped fully inside the host…
        let cx = Math.max(clampR + 8, Math.min(hr.width - clampR - 8, ev.clientX - hr.left));
        let cy = Math.max(clampR + 8, Math.min(hr.height - clampR - 8, ev.clientY - hr.top));
        // …and outside any game-reserved region.
        ({ x: cx, y: cy } = pushOutsideRects(cx, cy, clampR, reservedHostRects()));
        cx = Math.max(clampR, Math.min(hr.width - clampR, cx));
        cy = Math.max(clampR, Math.min(hr.height - clampR, cy));
        widget.style.left = `${cx - halfW}px`;
        widget.style.top = `${cy - halfH}px`;
        widget.style.right = 'auto';
        widget.style.bottom = 'auto';
        widget.classList.add('active');
        centerClientX = hr.left + cx;
        centerClientY = hr.top + cy;
        return { x: centerClientX, y: centerClientY };
      },
      onThumb: (x, y) => {
        const dx = x - centerClientX;
        const dy = xOnly ? 0 : y - centerClientY;
        thumb.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      },
      onHide: () => {
        // Snap back to the CSS-anchored home position (bottom-left,
        // safe-area padded).
        widget.classList.remove('active');
        widget.style.left = '';
        widget.style.top = '';
        widget.style.right = '';
        widget.style.bottom = '';
        thumb.style.transform = 'translate(-50%, -50%)';
      },
    });

    phaseCleanups.push(() => {
      core.dispose();
      zone.remove();
      widget.remove();
      if (joyEl === widget) joyEl = null;
    });
  }

  // ── Action buttons ──────────────────────────────────────────────────
  function buildButton(slot: ActionSlot, spec: ButtonSpec): void {
    const mode = spec.mode ?? 'tap';
    let pressed = false;
    let downAt = 0;
    let chargeTimer: number | null = null;

    const press = () => {
      if (pressed || buttonHidden[slot]) return;
      pressed = true;
      downAt = Date.now();
      buttonEls[slot]?.classList.add('pressed');
      emitAction(slot, 'press');
      if (mode === 'charge') {
        chargeTimer = window.setTimeout(() => releaseNow(), CHARGE_MAX_MS);
      }
    };
    const releaseNow = () => {
      if (!pressed) return;
      pressed = false;
      buttonEls[slot]?.classList.remove('pressed');
      if (chargeTimer !== null) { window.clearTimeout(chargeTimer); chargeTimer = null; }
      emitAction(slot, 'release', Math.min(Date.now() - downAt, mode === 'charge' ? CHARGE_MAX_MS : Number.MAX_SAFE_INTEGER));
    };

    if (isTouch) {
      if (!actionsEl) {
        actionsEl = document.createElement('div');
        actionsEl.className = 'tt-actions';
        layerEl.appendChild(actionsEl);
        const cluster = actionsEl;
        phaseCleanups.push(() => {
          cluster.remove();
          if (actionsEl === cluster) actionsEl = null;
        });
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `tt-action-btn tt-action-${slot}`;
      const glyph = spec.icon ? (ICON_GLYPHS[spec.icon] ?? '') : '';
      if (glyph) {
        const ic = document.createElement('span');
        ic.className = 'tt-action-icon';
        ic.textContent = glyph;
        btn.appendChild(ic);
      }
      const lbl = document.createElement('span');
      lbl.className = 'tt-action-label';
      lbl.textContent = spec.label;
      btn.appendChild(lbl);
      if (buttonHidden[slot]) btn.style.display = 'none';
      actionsEl.appendChild(btn);
      buttonEls[slot] = btn;

      const onDown = (ev: PointerEvent) => { ev.preventDefault(); btn.setPointerCapture?.(ev.pointerId); press(); };
      const onUp = (ev: PointerEvent) => { ev.preventDefault(); releaseNow(); };
      btn.addEventListener('pointerdown', onDown);
      btn.addEventListener('pointerup', onUp);
      btn.addEventListener('pointercancel', onUp);
      phaseCleanups.push(() => {
        btn.removeEventListener('pointerdown', onDown);
        btn.removeEventListener('pointerup', onUp);
        btn.removeEventListener('pointercancel', onUp);
        btn.remove();
        if (buttonEls[slot] === btn) buttonEls[slot] = null;
      });
    }

    // Key bindings work on every platform (hybrids included). Manifest
    // keys override the defaults (primary Space/KeyJ, secondary Shift/KeyK).
    const codes = spec.keys ?? DEFAULT_ACTION_KEYS[slot];
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || isTypingTarget(e) || !codes.includes(e.code)) return;
      e.preventDefault();
      press();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!codes.includes(e.code)) return;
      releaseNow();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    phaseCleanups.push(() => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      if (chargeTimer !== null) window.clearTimeout(chargeTimer);
    });
  }

  // ── Aux input (taps / drags / strokes / typing / aux keys) ──────────
  function ensureTypingInput(): HTMLInputElement {
    if (typingInput) return typingInput;
    const inp = document.createElement('input');
    inp.type = 'text';
    inp.className = 'tt-typing-input';
    inp.autocapitalize = 'off';
    inp.autocomplete = 'off';
    inp.spellcheck = false;
    inp.setAttribute('autocorrect', 'off');
    inp.addEventListener('keydown', (e) => {
      emitAux({ type: 'key', x: 0, y: 0, code: e.code || e.key });
    });
    layerEl.appendChild(inp);
    typingInput = inp;
    return inp;
  }

  function buildAux(eff: EffectiveLayer): void {
    if (eff.aux !== 'none') {
      const surface = document.createElement('div');
      surface.className = 'tt-aux-surface';
      // Insert first so move/button widgets stack above it.
      layerEl.insertBefore(surface, layerEl.firstChild);
      phaseCleanups.push(() => surface.remove());

      if (eff.aux === 'tap-world' || eff.aux === 'tap-target') {
        const onTap = (ev: PointerEvent) => {
          const r = surface.getBoundingClientRect();
          emitAux({ type: 'tap', x: ev.clientX - r.left, y: ev.clientY - r.top });
        };
        surface.addEventListener('pointerdown', onTap);
        phaseCleanups.push(() => surface.removeEventListener('pointerdown', onTap));
      } else if (eff.aux === 'drag') {
        phaseCleanups.push(bindDrag(surface, (e) => {
          emitAux({
            type: e.type === 'start' ? 'drag-start' : e.type === 'move' ? 'drag-move' : 'drag-end',
            x: e.x, y: e.y,
          });
        }));
      } else if (eff.aux === 'draw') {
        phaseCleanups.push(bindStroke(
          surface,
          (p) => emitAux({ type: 'stroke-point', x: p.x, y: p.y }),
          (stroke) => {
            const last = stroke[stroke.length - 1];
            emitAux({ type: 'stroke-end', x: last?.x ?? 0, y: last?.y ?? 0 });
          },
        ));
      } else if (eff.aux === 'typing') {
        const inp = ensureTypingInput();
        const onTap = () => inp.focus({ preventScroll: true });
        surface.addEventListener('pointerdown', onTap);
        phaseCleanups.push(() => surface.removeEventListener('pointerdown', onTap));
      }
    }

    if (eff.auxKeys && eff.auxKeys.length > 0) {
      const codes = eff.auxKeys;
      const onKey = (e: KeyboardEvent) => {
        if (e.repeat || isTypingTarget(e) || !codes.includes(e.code)) return;
        e.preventDefault();
        emitAux({ type: 'key', x: 0, y: 0, code: e.code });
      };
      window.addEventListener('keydown', onKey);
      phaseCleanups.push(() => window.removeEventListener('keydown', onKey));
    }
  }

  // ── Phase rebuild (atomic widget swap) ──────────────────────────────
  function rebuild(): void {
    for (const fn of phaseCleanups.splice(0)) { try { fn(); } catch { /* */ } }
    if (destroyed) return;
    const eff = resolveLayer(currentPhase);
    buildAux(eff);
    buildMove(eff);
    if (eff.primary) buildButton('primary', eff.primary);
    if (eff.secondary) buildButton('secondary', eff.secondary);
  }

  // ── Desktop hint strip (one per game, dismissible, fades after 4s) ──
  const hintTimers: number[] = [];
  if (!isTouch) {
    const storageKey = `tt-controls-hint-${opts.gameId ?? 'game'}`;
    let dismissed = false;
    try { dismissed = localStorage.getItem(storageKey) === '1'; } catch { /* */ }
    if (!dismissed) {
      const parts: string[] = [];
      if ((scheme.move ?? 'none') !== 'none') parts.push('WASD / Arrows — Move');
      const keyList = (codes: string[]) => codes.map(prettyKeyShort).join(' / ');
      if (scheme.primary) parts.push(`${keyList(scheme.primary.keys ?? DEFAULT_ACTION_KEYS.primary)} — ${scheme.primary.label}`);
      if (scheme.secondary) parts.push(`${keyList(scheme.secondary.keys ?? DEFAULT_ACTION_KEYS.secondary)} — ${scheme.secondary.label}`);
      if (scheme.auxKeys && scheme.auxKeys.length > 0) {
        const shown = scheme.auxKeys.slice(0, 4).map(prettyKeyShort).join(' ');
        parts.push(`${shown}${scheme.auxKeys.length > 4 ? '…' : ''} — Act`);
      }
      if (parts.length > 0) {
        const hint = document.createElement('div');
        hint.className = 'tt-controls-hint';
        const text = document.createElement('span');
        text.textContent = parts.join('  ·  ');
        hint.appendChild(text);
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'tt-hint-dismiss';
        close.textContent = '✕';
        close.setAttribute('aria-label', 'Dismiss controls hint');
        close.addEventListener('click', () => {
          try { localStorage.setItem(storageKey, '1'); } catch { /* */ }
          hint.remove();
        });
        hint.appendChild(close);
        layerEl.appendChild(hint);
        hintTimers.push(window.setTimeout(() => hint.classList.add('faded'), 4000));
        hintTimers.push(window.setTimeout(() => hint.remove(), 4900));
      }
    }
  }

  // ── Public objects ──────────────────────────────────────────────────
  const controls: GameControls = {
    move: moveState,
    on(action, ev, cb) {
      actionSubs[action][ev].add(cb);
      return () => { actionSubs[action][ev].delete(cb); };
    },
    onAux(cb) {
      auxSubs.add(cb);
      return () => { auxSubs.delete(cb); };
    },
    setPhase(name) {
      currentPhase = name;
      rebuild();
    },
    setButtonVisible(slot, visible) {
      buttonHidden[slot] = !visible;
      const el = buttonEls[slot];
      if (el) el.style.display = visible ? '' : 'none';
    },
    reserveRegions(rects) {
      reserved = rects.map((r) => ({ ...r }));
    },
    openTray(el) {
      trayEl.replaceChildren(el);
      trayEl.classList.add('open');
    },
    closeTray() {
      trayEl.classList.remove('open');
      trayEl.replaceChildren();
    },
    focusTyping() {
      ensureTypingInput().focus({ preventScroll: true });
    },
  };

  function getControlsRect(): HostRect | null {
    if (!isTouch) return null;
    const hr = host.getBoundingClientRect();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const el of [joyEl, actionsEl]) {
      if (!el || el.style.display === 'none') continue;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      minX = Math.min(minX, r.left - hr.left);
      minY = Math.min(minY, r.top - hr.top);
      maxX = Math.max(maxX, r.right - hr.left);
      maxY = Math.max(maxY, r.bottom - hr.top);
    }
    if (!Number.isFinite(minX)) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  rebuild();

  return {
    controls,
    layerEl,
    getControlsRect,
    setStageTransform(fn) {
      stageTransform = fn;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      for (const fn of phaseCleanups.splice(0)) { try { fn(); } catch { /* */ } }
      for (const t of hintTimers.splice(0)) window.clearTimeout(t);
      heldKeys.dispose();
      auxSubs.clear();
      for (const slot of ['primary', 'secondary'] as const) {
        actionSubs[slot].press.clear();
        actionSubs[slot].release.clear();
      }
      typingInput = null;
      layerEl.remove();
      opts.onDestroy?.();
    },
  };
}
